import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import {
  InventoryCostingApiService,
  InventoryCostValuation,
  InventoryValuationHistoryItem
} from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-valuation',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule,
    ButtonModule, SelectModule, InputTextModule, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="t('catalog.costing.valuation.title')" [description]="t('catalog.costing.valuation.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.valuation.help.title'"
        [descriptionKey]="'catalog.costing.valuation.help.description'"
        [bulletKeys]="['catalog.costing.valuation.help.bullets.live', 'catalog.costing.valuation.help.bullets.snapshot']" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
      } @else {
        <section class="panel">
          <h3>{{ t('catalog.costing.valuation.currentTitle') }}
            <ain-info-hint [titleKey]="'catalog.costing.valuation.currentHelp.title'" [descriptionKey]="'catalog.costing.valuation.currentHelp.description'" />
          </h3>
          <div class="summary">
            <article><strong>{{ t('columns.currency') }}</strong><span>{{ valuation()?.currencyCode || '-' }}</span></article>
            <article><strong>{{ t('catalog.costing.valuation.totalQuantity') }}</strong><span>{{ valuation()?.totalQuantity ?? 0 }}</span></article>
            <article><strong>{{ t('catalog.costing.valuation.totalCost') }}</strong><span>{{ valuation()?.totalCost ?? 0 }}</span></article>
          </div>

          <details class="filters" [open]="true">
            <summary>{{ t('catalog.filter.show') }}</summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="offeringOptions()" [(ngModel)]="filters.offeringId" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" (ngModelChange)="loadValuation()" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.warehouseId') }}</span>
                <p-select [options]="warehouseOptions()" [(ngModel)]="filters.warehouseId" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="loadValuation()" />
              </label>
            </div>
          </details>

          <p-table [value]="valuation()?.lines ?? []" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
                <th>{{ t('catalog.costing.valuation.totalCost') }}</th>
                <th>{{ t('catalog.costing.valuation.avgUnitCost') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ resolveOffering(row.offeringId) }}</td>
                <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                <td>{{ row.quantity }}</td>
                <td>{{ row.totalCost }}</td>
                <td>{{ row.averageUnitCost }}</td>
              </tr>
            </ng-template>
          </p-table>

          <div class="capture-row" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'">
            <label>
              <span>{{ t('catalog.costing.valuation.captureReference') }}</span>
              <input pInputText [(ngModel)]="captureReference" />
            </label>
            <button pButton type="button" [label]="t('catalog.costing.valuation.captureRun')" [disabled]="capturing()" (click)="captureRun()"></button>
          </div>
        </section>

        <section class="panel">
          <h3>{{ t('catalog.costing.valuation.historyTitle') }}
            <ain-info-hint [titleKey]="'catalog.costing.valuation.historyHelp.title'" [descriptionKey]="'catalog.costing.valuation.historyHelp.description'" />
          </h3>
          <p-table [value]="history()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.reference') }}</th>
                <th>{{ t('catalog.costing.valuation.runAt') }}</th>
                <th>{{ t('catalog.costing.valuation.triggerSource') }}</th>
                <th>{{ t('catalog.costing.valuation.totalCost') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><button type="button" class="link-btn" (click)="openSnapshot(row.snapshotId)">{{ row.referenceNumber }}</button></td>
                <td>{{ row.runAtUtc }}</td>
                <td>{{ row.triggerSource }}</td>
                <td>{{ row.totalCost }} {{ row.currencyCode }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 12px; }
    .panel h3 { display: flex; align-items: center; gap: 6px; margin: 0 0 10px; }
    .summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px; margin-bottom: 12px; }
    .summary article { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; display: grid; gap: 4px; }
    .filters { margin-bottom: 10px; }
    .filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .capture-row { display: flex; gap: 10px; align-items: end; margin-top: 12px; flex-wrap: wrap; }
    .capture-row label { display: grid; gap: 6px; min-width: 220px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryValuationComponent implements OnInit {
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly capturing = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly valuation = signal<InventoryCostValuation | null>(null);
  readonly history = signal<InventoryValuationHistoryItem[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  filters = { offeringId: '', warehouseId: '' };
  captureReference = '';

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.costingApi.getValuation({
      offeringId: this.filters.offeringId || undefined,
      warehouseId: this.filters.warehouseId || undefined
    }).subscribe({
      next: val => {
        this.valuation.set(val);
        this.loadHistory(showLoading);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('catalog.costing.states.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  loadValuation(): void {
    this.costingApi.getValuation({
      offeringId: this.filters.offeringId || undefined,
      warehouseId: this.filters.warehouseId || undefined
    }).subscribe({
      next: val => this.valuation.set(val),
      error: () => this.error.set(this.t('catalog.costing.states.loadFailed'))
    });
  }

  loadHistory(finishLoading = false): void {
    this.costingApi.listValuationHistory().subscribe({
      next: rows => {
        this.history.set(rows);
        if (finishLoading) this.loading.set(false);
      },
      error: () => {
        this.history.set([]);
        if (finishLoading) this.loading.set(false);
      }
    });
  }

  captureRun(): void {
    const ref = this.captureReference.trim();
    if (!ref) {
      this.error.set(this.t('catalog.costing.valuation.referenceRequired'));
      return;
    }
    this.capturing.set(true);
    this.costingApi.captureValuationRun({
      referenceNumber: ref,
      triggerSource: 'Manual',
      offeringId: this.filters.offeringId || null,
      warehouseId: this.filters.warehouseId || null
    }).subscribe({
      next: () => {
        this.capturing.set(false);
        this.captureReference = '';
        this.load(false);
      },
      error: () => {
        this.capturing.set(false);
        this.error.set(this.t('catalog.costing.valuation.captureFailed'));
      }
    });
  }

  openSnapshot(snapshotId: string): void {
    void this.router.navigate(['/inventory/costing/valuation/snapshots', snapshotId]);
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));

  resolveOffering(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : '-';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
